<template>
    <div class="d-flex flex-row flex-nowrap">
        <div class="flex-fill flex-grow-1">
            <component
                :is="params.comp"
                v-if="params.data !== undefined && val !== undefined && (params.value !== undefined || 'isCheckboxColumn' in params)"
                class="eq-grid-cell-renderer"
                v-bind="getComponentProps()"
                :value="val"
                :row="params.data"
                :grid="params.grid"
                :rowId="params.node.id"
                @validation="onValidation"
                ref="innerComp"
            />
        </div>
        <div
            v-if="alertMessage !== null"
            class="flex-shrink-1 border border-1 border-end-0 border-top-0 border-bottom-0"
            :class="{
                'border-warning': alertMessage.type == 'warning',
                'border-danger': alertMessage.type == 'danger',
            }"
        >
            <TooltipComp :text="$ti(alertMessage.message, { field: '' }, true)">
                <span
                    class="alert alert-badge alert-badge-grid ms-1 border-0 ps-1 d-flex align-items-center justify-content-center"
                    :class="{
                        'alert-warning': alertMessage.type == 'warning',
                        'alert-danger': alertMessage.type == 'danger',
                    }"
                    ><IconComp :icon="alertMessage.type == 'warning' ? featureIcons.WARNING : 'fas fa-exclamation'"
                /></span>
            </TooltipComp>
        </div>
    </div>
</template>
<script setup lang="ts" generic="TRowData, TCell = any">
import { type ICellRendererParams } from 'ag-grid-community';
import type { ICellRendererComponentParams } from '../Classes/params';
import { useAgGridCellValueComputedWrapper } from '../Classes/AgGridCellValueComputedWrapper';
import { watch } from 'vue';
import type { IAlertMessage } from '@/objects';
import { ref } from 'vue';
import { IconComp, TooltipComp } from '@/components';
import { featureIcons } from '@/components/Menu/featureicons';
import { $ti } from '@/locize';

const props = defineProps<{
    params: ICellRendererParams & ICellRendererComponentParams<TRowData, TCell>;
}>();

const alertMessage = ref<IAlertMessage | null>(null);

/**
 * Gets the component props.
 * This is a function instead of just a variable so we dont evaluate it until the component actually renders, and the data is available.
 */
const getComponentProps = () =>
    props.params.componentProps instanceof Function ? props.params.componentProps(props.params.data) : props.params.componentProps;

// We use a computed for the value and get it from the data object which represents the row.
// This is so the inner component can keep the vue reactivity instead of losing it since the grid will only supply the primitive value in params.value.
const val = useAgGridCellValueComputedWrapper(props.params);

// If we have a cellRefreshTrigger we refresh the cell when the trigger triggers.
if (props.params.cellRefreshTrigger && props.params.refreshCell) {
    watch(
        () => {
            if (!props.params.data) {
                return '';
            }
            return props.params.cellRefreshTrigger(props.params.data, val.value);
        },
        () => {
            props.params.refreshCell!();
        },
    );
}

if (props.params.rowRefreshTrigger) {
    watch(
        () => {
            if (!props.params.data) {
                return '';
            }
            return props.params.rowRefreshTrigger(props.params.data);
        },
        () => {
            // Remark. This one messes with the edititing state and should be used with care
            // We remove it for now until we find a better way to do it
            // props.params.api.redrawRows({ rowNodes: [props.params.node] });
        },
    );
}

/**
 * If there is raised a validation event from the inner component
 * @param message
 */
const onValidation = (message: IAlertMessage | null) => {
    alertMessage.value = message;
};
</script>
